import type { DragEndEvent } from "@dnd-kit/core";
import type { QueryClient } from "@tanstack/react-query";
import {
  linkLibraryDocumentToSpace,
  linkLibraryDocumentToTopic,
  moveLibraryDocumentToFolder,
  type LibraryDndItemType,
} from "@/library/libraryDocumentDndActions";
import { runLibraryFolderTreeDragEnd, type LibraryFolderTreeDragState } from "@/library/libraryFolderTreeDragEnd";

export const libraryDocumentDropHooksRef = {
  current: null as null | {
    onOptimisticMove: (itemId: string) => void;
    onClearOptimisticMove: (itemId: string) => void;
  },
};

export const libraryFolderTreeDragStateRef = {
  current: null as LibraryFolderTreeDragState | null,
};

/** Library sidebar registers drag UI handlers (active/over highlights for folder tree). */
export const librarySidebarDndUiRef = {
  current: null as null | {
    onDragStart: (event: import("@dnd-kit/core").DragStartEvent) => void;
    onDragOver: (event: import("@dnd-kit/core").DragOverEvent) => void;
    resetUi: () => void;
  },
};

function resolveItemType(active: DragEndEvent["active"]): LibraryDndItemType | null {
  const cur = active.data.current as { itemType?: LibraryDndItemType; documentType?: string } | undefined;
  if (cur?.itemType === "note" || cur?.itemType === "reference") return cur.itemType;
  if (cur?.documentType === "note") return "note";
  if (cur?.documentType === "reference") return "reference";
  return null;
}

function isUuidLikeFolderId(id: string, folders: LibraryFolderTreeDragState["folders"]): boolean {
  return folders.some((f) => f.id === id);
}

function parseDocumentFolderTarget(
  overIdStr: string,
  folders: LibraryFolderTreeDragState["folders"]
): string | null {
  if (overIdStr.startsWith("droppable-space-") || overIdStr.startsWith("droppable-topic-")) return null;
  if (overIdStr.startsWith("droppable-")) {
    return overIdStr.replace("droppable-", "");
  }
  if (!overIdStr.startsWith("doc-") && isUuidLikeFolderId(overIdStr, folders)) {
    return overIdStr;
  }
  return null;
}

export type LibraryRouteToast = (opts: {
  title: string;
  description?: string;
  variant?: "destructive";
}) => void;

export async function handleLibraryRouteDragEnd(
  event: DragEndEvent,
  deps: {
    queryClient: QueryClient;
    toast: LibraryRouteToast;
  }
): Promise<void> {
  const { queryClient, toast } = deps;
  const { active, over } = event;
  if (!over || active.id === over.id) return;

  const activeIdStr = String(active.id);
  const overIdStr = String(over.id);

  if (activeIdStr.startsWith("doc-")) {
    const documentId = activeIdStr.replace("doc-", "");
    const itemType = resolveItemType(active);
    if (!itemType) {
      toast({ title: "Cannot move item", description: "Unknown item type.", variant: "destructive" });
      return;
    }

    const hooks = libraryDocumentDropHooksRef.current;
    const runWithOptimistic = async (fn: () => Promise<void>, successToast: { title: string; description?: string }) => {
      hooks?.onOptimisticMove(documentId);
      try {
        await fn();
        hooks?.onClearOptimisticMove(documentId);
        toast(successToast);
      } catch (e) {
        hooks?.onClearOptimisticMove(documentId);
        toast({
          title: "Error moving item",
          description: e instanceof Error ? e.message : "Failed to move",
          variant: "destructive",
        });
      }
    };

    if (overIdStr.startsWith("droppable-space-")) {
      const spaceId = overIdStr.replace("droppable-space-", "");
      await runWithOptimistic(
        () => linkLibraryDocumentToSpace(queryClient, documentId, itemType, spaceId),
        { title: "Item moved", description: "The item has been linked to the space." }
      );
      return;
    }

    if (overIdStr.startsWith("droppable-topic-")) {
      const topicId = overIdStr.replace("droppable-topic-", "");
      const topicHomeSpaceId = (over.data.current as { spaceId?: string } | undefined)?.spaceId ?? null;
      await runWithOptimistic(
        () => linkLibraryDocumentToTopic(queryClient, documentId, itemType, topicId, topicHomeSpaceId),
        { title: "Item moved", description: "The item has been linked to the topic." }
      );
      return;
    }

    const tree = libraryFolderTreeDragStateRef.current;
    const folderTarget = tree ? parseDocumentFolderTarget(overIdStr, tree.folders) : null;
    if (folderTarget) {
      await runWithOptimistic(
        () => moveLibraryDocumentToFolder(queryClient, documentId, folderTarget, itemType),
        { title: "Item moved", description: "The item has been moved to the folder." }
      );
      return;
    }

    return;
  }

  const treeState = libraryFolderTreeDragStateRef.current;
  if (treeState) {
    await runLibraryFolderTreeDragEnd(event, treeState, queryClient, toast);
  }
}
